import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { DETAILED_ATTENDANCE_REPORT } from "../../constants/api";
import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import dayjs from "dayjs";
import { cn } from "../../utils/tailwindUtils";

const ISSUE_BADGE = {
    "Single punch": "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    "Odd punch": "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    "Short duty": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    "Late penalty": "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
    "Missing check-in": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    "Missing check-out": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    "Missing punches": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    "In progress": "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
};

const IssueFlags = ({ flags }) => {
    if (!flags?.length) {
        return <span className="text-gray-400 text-xs">—</span>;
    }
    return (
        <div className="flex flex-wrap gap-1">
            {flags.map((flag) => (
                <span
                    key={flag}
                    className={cn(
                        "text-[10px] font-medium px-1.5 py-0.5 rounded",
                        ISSUE_BADGE[flag] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    )}
                >
                    {flag}
                </span>
            ))}
        </div>
    );
};

const DetailedAttendanceReport = () => {
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [exceptionsOnly, setExceptionsOnly] = useState(false);
    const navigate = useNavigate();

    const columns = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "280px",
            Cell: ({ row }) => {
                const record = row;
                const empDetails = {
                    ...record?.employee,
                    designation_id: record?.employee?.designation
                        ? {
                            id: record.employee.designation.id,
                            designation_name: record.employee.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee?.employee_name || "-",
                    id: record?.employee?.employee_id || "-",
                };
                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "date",
            Header: "Date",
            Cell: ({ value }) => (value ? dayjs(value).format("DD MMM YYYY") : "-"),
        },
        {
            accessor: "status",
            Header: "Status",
            filterable: true,
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "shift",
            Header: "Shift",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "check_in",
            Header: "Check In",
            Cell: ({ value }) =>
                value && dayjs(value).isValid() ? dayjs(value).format("HH:mm") : "-",
        },
        {
            accessor: "check_out",
            Header: "Check Out",
            Cell: ({ value }) =>
                value && dayjs(value).isValid() ? dayjs(value).format("HH:mm") : "-",
        },
        {
            accessor: "total_working_hours",
            Header: "Hours",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "late_hours",
            Header: "Late",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "overtime",
            Header: "OT",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "issue_flags",
            Header: "Exceptions",
            Cell: ({ value }) => <IssueFlags flags={value} />,
        },
        {
            accessor: "has_correction",
            Header: "Corrected",
            Cell: ({ value }) => (
                value ? (
                    <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Yes</span>
                ) : (
                    <span className="text-xs text-gray-400">—</span>
                )
            ),
        },
        {
            accessor: "location",
            Header: "Location",
            Cell: ({ value }) => value ?? "-",
        },
    ];

    return (
        <ContainerCard className="font-sans">
            <div className="flex justify-between items-center mb-4 shrink-0">
                <span className="flex gap-2 items-center">
                    <ChevronLeftIcon
                        onClick={() => navigate("/reports")}
                        className="cursor-pointer"
                    />
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Detailed Attendance Report ({totalRecords})
                    </h1>
                </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 shrink-0">
                Daily attendance with exception flags, penalties, and correction status for a selected period.
            </p>

            <label className="inline-flex items-center gap-2 mb-3 text-sm text-gray-600 dark:text-gray-400 cursor-pointer shrink-0">
                <input
                    type="checkbox"
                    checked={exceptionsOnly}
                    onChange={(e) => setExceptionsOnly(e.target.checked)}
                    className="rounded border-gray-300"
                />
                Show rows with attendance exceptions only
            </label>

            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                url={DETAILED_ATTENDANCE_REPORT}
                serverSide={true}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isFromToDateRequired={true}
                printFilter={exceptionsOnly ? { exceptions_only: "true" } : {}}
                filter={exceptionsOnly ? { exceptions_only: "true" } : {}}
            />
        </ContainerCard>
    );
}

export default DetailedAttendanceReport;
